import { ActionIcon, Box, Group, Image, Modal, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    urls: string[];
    index: number;
    opened: boolean;
    onClose: () => void;
    onIndexChange?: (index: number) => void;
    title?: string | null;
};

export const ImageLightbox = ({
    urls,
    index,
    opened,
    onClose,
    onIndexChange,
    title,
}: Props) => {
    const { t } = useTranslation();
    const list = urls.filter(Boolean);
    const safeIndex = list.length ? Math.min(Math.max(index, 0), list.length - 1) : 0;
    const current = list[safeIndex];
    const multi = list.length > 1;

    useEffect(() => {
        if (!opened || !multi || !onIndexChange) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                onIndexChange((safeIndex - 1 + list.length) % list.length);
            } else if (e.key === 'ArrowRight') {
                onIndexChange((safeIndex + 1) % list.length);
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opened, multi, onIndexChange, safeIndex, list.length]);

    if (!current) return null;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size='xl'
            centered
            padding='md'
            withCloseButton={false}
            title={
                <Group justify='space-between' wrap='nowrap' style={{ width: '100%' }}>
                    <Text size='sm' fw={600} truncate>
                        {title || t('components.imageLightbox.defaultTitle')}
                        {multi ? ` · ${safeIndex + 1}/${list.length}` : ''}
                    </Text>
                    <ActionIcon
                        variant='subtle'
                        onClick={onClose}
                        aria-label={t('components.imageLightbox.close')}
                    >
                        <IconX size={16} />
                    </ActionIcon>
                </Group>
            }
        >
            <Box style={{ position: 'relative' }}>
                <Image
                    src={current}
                    alt={title || t('components.imageLightbox.alt')}
                    mah='75vh'
                    fit='contain'
                    mx='auto'
                    radius='sm'
                />
                {multi && onIndexChange ? (
                    <>
                        <ActionIcon
                            variant='filled'
                            color='dark'
                            size='lg'
                            radius='xl'
                            aria-label={t('components.imageLightbox.previous')}
                            onClick={() =>
                                onIndexChange((safeIndex - 1 + list.length) % list.length)
                            }
                            style={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        >
                            <IconChevronLeft size={18} />
                        </ActionIcon>
                        <ActionIcon
                            variant='filled'
                            color='dark'
                            size='lg'
                            radius='xl'
                            aria-label={t('components.imageLightbox.next')}
                            onClick={() => onIndexChange((safeIndex + 1) % list.length)}
                            style={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        >
                            <IconChevronRight size={18} />
                        </ActionIcon>
                    </>
                ) : null}
            </Box>
            {multi && onIndexChange ? (
                <Group gap={6} mt='md' justify='center' wrap='wrap'>
                    {list.map((url, i) => (
                        <UnstyledButton
                            key={`${url}-${i}`}
                            onClick={() => onIndexChange(i)}
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                overflow: 'hidden',
                                border:
                                    i === safeIndex
                                        ? '2px solid var(--mantine-color-brand-5, var(--mantine-color-blue-5))'
                                        : '1px solid var(--mantine-color-gray-4)',
                                opacity: i === safeIndex ? 1 : 0.75,
                            }}
                        >
                            <Image src={url} alt='' w={56} h={56} fit='cover' />
                        </UnstyledButton>
                    ))}
                </Group>
            ) : null}
        </Modal>
    );
};
